import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ActiveEmployerCompanyService } from '../../../services/active-employer-company/active-employer-company.service';
import { PayrollAccessService } from '../../../services/payroll-access/payroll-access.service';
import { TeamsService } from '../../../services/teams/teams.service';
import { UsersService } from '../../../services/users/users.service';
import { NativeTeamContextService } from '../../../services/native-team-context/native-team-context.service';

@Component({
    selector: 'app-payroll-expense-approvals',
    templateUrl: './payroll-expense-approvals.component.html',
    styleUrls: ['./payroll-expense-approvals.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PayrollExpenseApprovalsComponent implements OnInit, OnDestroy {
  public selectedTabIndex = 0;
  public selectedTeamId = '';
  public isInitialCompanyCheckComplete = false;
  public isTeamsLoading = false;
  public hasCompany = false;
  public isNativeCompanyManager = false;
  public teams: any[] = [];
  private currentCompany?: any;
  private loadedCompanyId = '';
  private isActiveCompanyReloadPending = false;
  private activeEmployerCompanySubscription?: Subscription;
  private meSubscription?: Subscription;

  constructor(
    private usersService: UsersService,
    private teamsService: TeamsService,
    private router: Router,
    private activeEmployerCompanyService: ActiveEmployerCompanyService,
    private payrollAccessService: PayrollAccessService,
    private nativeTeamContextService: NativeTeamContextService,
  ) {}

  ngOnInit(): void {
    this.isNativeCompanyManager = this.payrollAccessService.workspaceContext === 'nativeCompanyManager';
    this.meSubscription = this.usersService.meSubscribe(async (me: any) => {
      if (!me) {
        return;
      }

      const cachedCompanies = this.isNativeCompanyManager
        ? undefined
        : this.usersService.getCachedTeamCompanies();
      if (cachedCompanies) {
        this.updateCompanyState(cachedCompanies);
        this.isInitialCompanyCheckComplete = true;
      }

      try {
        await this.loadPageData(!cachedCompanies);
      } finally {
        this.isInitialCompanyCheckComplete = true;
      }

      this.subscribeToActiveEmployerCompany();
    });
  }

  ngOnDestroy(): void {
    this.meSubscription?.unsubscribe();
    this.activeEmployerCompanySubscription?.unsubscribe();
  }

  get selectedTeam(): any {
    return this.teams.find((team) => team?.id === this.selectedTeamId) || this.teams[0] || null;
  }

  onTabChange(index: number): void {
    this.selectedTabIndex = index;
  }

  async onSelectedTeamChange(teamId: string): Promise<void> {
    const authorizedTeam = this.teams.find(
      (team) => `${team?.id || ''}` === `${teamId || ''}`.trim(),
    );
    if (!authorizedTeam?.id) {
      return;
    }
    if (this.isNativeCompanyManager) {
      const selectedTeam = await this.nativeTeamContextService.selectTeam(authorizedTeam.id);
      if (`${selectedTeam?.id || ''}` !== `${authorizedTeam.id}`) {
        return;
      }
    }
    this.selectedTeamId = authorizedTeam.id;
  }

  onGoToCompanyTab(): void {
    void this.router.navigate(['payrolladmin', 'company', 'company']);
  }

  onGoToTeams(): void {
    void this.router.navigate(['payrolladmin', 'teams']);
  }

  private async loadPageData(forceRefresh = false): Promise<void> {
    this.isTeamsLoading = true;

    try {
      const companies = this.isNativeCompanyManager
        ? this.toActiveCompanyList(await this.activeEmployerCompanyService.initialize(forceRefresh))
        : await this.usersService.getTeamCompanies(forceRefresh);
      this.updateCompanyState(companies);

      if (!this.hasCompany) {
        this.teams = [];
        this.selectedTeamId = '';
        this.loadedCompanyId = '';
        return;
      }

      const teams = await this.teamsService.getTeamsForCompany(this.currentCompany.id);
      this.teams = Array.isArray(teams) ? teams : [];
      this.loadedCompanyId = `${this.currentCompany.id || ''}`;
      const selectedTeamStillExists = this.teams.some((team) => team?.id === this.selectedTeamId);
      const globalTeamId = this.isNativeCompanyManager
        ? `${this.teamsService.getSelectedWellspaceCandidateId() || ''}`.trim()
        : '';
      const globalTeam = globalTeamId
        ? this.teams.find((team) => `${team?.id || ''}` === globalTeamId)
        : undefined;
      this.selectedTeamId = selectedTeamStillExists
        ? this.selectedTeamId
        : (globalTeam?.id ?? this.teams[0]?.id ?? '');
    } finally {
      this.isTeamsLoading = false;
    }
  }

  private updateCompanyState(companies: any[]): void {
    this.hasCompany = companies.length > 0;
    this.currentCompany = this.hasCompany ? companies[0] : undefined;
  }

  private subscribeToActiveEmployerCompany(): void {
    if (!this.isNativeCompanyManager || this.activeEmployerCompanySubscription) {
      return;
    }

    this.activeEmployerCompanySubscription = this.activeEmployerCompanyService.stateChanges().subscribe((state) => {
      const nextCompanyId = `${state.activeCompany?.id || ''}`;
      if (
        !state.initialized
        || state.loading
        || !nextCompanyId
        || nextCompanyId === this.loadedCompanyId
        || this.isActiveCompanyReloadPending
      ) {
        return;
      }

      this.isActiveCompanyReloadPending = true;
      void this.loadPageData().finally(() => {
        this.isActiveCompanyReloadPending = false;
      });
    });
  }

  private toActiveCompanyList(state: { activeCompany?: any }): any[] {
    return state?.activeCompany ? [state.activeCompany] : [];
  }
}
